import { mobilePhoneSchema } from '@/models/MobilePhone';
import { emptyMobilePhone, type MobilePhoneValue } from '@/utils/phoneNumber';
import { toTitleCase } from '@/utils/format';
import { RoleRestriction } from '../enums';
import { z } from 'zod';

export type InviteUserType = string;

const PERSON_NAME_PROHIBITED_CHARACTERS = /[<>&"$%!#?§;*~/\\|^=[\]{}()\p{Cc}]/u;

export const hasValidPersonNameCharacters = (value: string): boolean =>
  !PERSON_NAME_PROHIBITED_CHARACTERS.test(value);

export const personNameCharactersMessage = (field: 'nombre' | 'apellido') =>
  `El ${field} no puede contener símbolos como < > & " $ % ! # ? § ; * ~ / \\ | ^ = [ ] { } ( )`;

export interface InviteUserFormData {
  firstName: string;
  lastName: string;
  email: string;
  phone?: MobilePhoneValue;
  userType: InviteUserType;
  roleIds: string[];
  operators: {
    includeAll: boolean;
    selected: string[];
    subOperators: {
      includeAll: boolean;
      selected: string[];
      excluded: string[];
    };
    excluded: string[];
  };
  commerces: {
    includeAll: boolean;
    selected: string[];
    excluded: string[];
  };
  branches: {
    includeAll: boolean;
    selected: string[];
    excluded: string[];
    requiredForSubOperator?: boolean;
  };
}

export const inviteUserOperatorsBaseSchema = z.object({
  includeAll: z.boolean(),
  selected: z.array(z.string()),
  subOperators: z.object({
    includeAll: z.boolean(),
    selected: z.array(z.string()),
    excluded: z.array(z.string()),
  }),
  excluded: z.array(z.string()),
});

const inviteUserOperatorsSchema = inviteUserOperatorsBaseSchema.superRefine(
  (data, ctx) => {
    if (!data.includeAll && data.selected.length === 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['selected'],
        message: 'Debes seleccionar al menos un operador logístico',
      });
    }
  }
);

export const inviteUserCommercesSchema = z.object({
  includeAll: z.boolean(),
  selected: z.array(z.string()),
  excluded: z.array(z.string()),
});

export const inviteUserBranchesSchema = z.object({
  includeAll: z.boolean(),
  selected: z.array(z.string()),
  excluded: z.array(z.string()),
  requiredForSubOperator: z.boolean().optional(),
});

/** Solo operadores / comercios / sucursales (misma forma que en invitar usuario). */
export const inviteUserAccessOnlySchema = z.object({
  operators: inviteUserOperatorsSchema,
  commerces: inviteUserCommercesSchema,
  branches: inviteUserBranchesSchema,
});

export type InviteUserAccessOnlySchema = z.infer<typeof inviteUserAccessOnlySchema>;

export function applyAccessScopeRefinements(
  userType: InviteUserType | undefined,
  data: {
    operators: { includeAll: boolean; selected: string[] };
    commerces: { includeAll: boolean; selected: string[] };
    branches: {
      includeAll: boolean;
      selected: string[];
      requiredForSubOperator?: boolean;
    };
  },
  ctx: z.RefinementCtx
) {
  if (userType === RoleRestriction.COMMERCE) {
    if (data.commerces.includeAll || data.branches.includeAll) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['commerces', 'selected'],
        message:
          'Un usuario de comercio debe tener al menos un comercio o sucursal concreto, no "todos"',
      });
    }
    const hasSpecificCommerce = data.commerces.selected.length > 0;
    const hasSpecificBranch = data.branches.selected.length > 0;
    if (!hasSpecificCommerce && !hasSpecificBranch) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['commerces', 'selected'],
        message: 'Debes seleccionar al menos un comercio o sucursal',
      });
    }
    return;
  }
  if (userType === RoleRestriction.EXTERNAL_OPERATOR) {
    if (data.operators.includeAll || data.operators.selected.length !== 1) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['operators', 'selected'],
        message: 'Debes seleccionar un único operador logístico',
      });
    }
    if (
      data.branches.requiredForSubOperator &&
      data.branches.selected.length !== 1
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['branches', 'selected'],
        message: 'Seleccioná la sucursal que atiende el sub-operador',
      });
    }
    return;
  }
  if (!data.operators.includeAll && data.operators.selected.length === 0) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['operators', 'selected'],
      message: 'Debes seleccionar al menos un operador logístico',
    });
  }
}

/** Valores por defecto del bloque operadores / comercios / sucursales (invitación, edición, etc.). */
export const defaultUserAccessScopeValues: InviteUserAccessOnlySchema = {
  operators: {
    includeAll: false,
    selected: [],
    subOperators: {
      includeAll: false,
      selected: [],
      excluded: [],
    },
    excluded: [],
  },
  commerces: {
    includeAll: false,
    selected: [],
    excluded: [],
  },
  branches: {
    includeAll: false,
    selected: [],
    excluded: [],
  },
};

/**
 * Campos crudos del formulario de invitación, sin las validaciones cruzadas de
 * scopes. Se expone aparte para que otros flujos (p. ej. el wizard de
 * integración de comercios) reusen las mismas reglas por campo.
 */
export const inviteUserFieldsSchema = z.object({
  firstName: z
    .string()
    .trim()
    .min(1, 'El nombre es obligatorio')
    .min(2, 'El nombre debe tener al menos 2 caracteres')
    .max(50, 'El nombre es demasiado largo')
    .refine(hasValidPersonNameCharacters, personNameCharactersMessage('nombre'))
    .transform(toTitleCase),
  lastName: z
    .string()
    .trim()
    .min(1, 'El apellido es obligatorio')
    .min(2, 'El apellido debe tener al menos 2 caracteres')
    .max(50, 'El apellido es demasiado largo')
    .refine(hasValidPersonNameCharacters, personNameCharactersMessage('apellido'))
    .transform(toTitleCase),
  email: z
    .string()
    .trim()
    .min(1, 'El email es obligatorio')
    .email('Email inválido'),
  phone: mobilePhoneSchema,
  userType: z.string().min(1, 'Selecciona el tipo de usuario'),
  roleIds: z.array(z.string()).min(1, 'Debes seleccionar al menos un rol'),
  operators: inviteUserOperatorsBaseSchema,
  commerces: inviteUserCommercesSchema,
  branches: inviteUserBranchesSchema,
});

export const inviteUserSchema = inviteUserFieldsSchema.superRefine(
  (data, ctx) => applyAccessScopeRefinements(data.userType, data, ctx)
);

export type InviteUserSchema = z.infer<typeof inviteUserSchema>;

export const defaultInviteUserValues: InviteUserSchema = {
  firstName: '',
  lastName: '',
  email: '',
  phone: emptyMobilePhone(),
  userType: '',
  roleIds: [],
  ...defaultUserAccessScopeValues,
};

export interface ScopePayload {
  scopeType: 'operator' | 'commerce' | 'branch';
  scopeId: string | null;
}

export interface ExclusionPayload {
  scopeType: 'operator' | 'commerce' | 'branch';
  scopeId: string;
}
